/**
 * Thin client for POST /answer.
 *
 * The contract is shared with rag/answer.ts (AnswerResponse / AnswerCitation).
 * Field-set guarantee comes from Task 044 (Citation Discipline) — every
 * stored citation field reaches the wire and the UI renders whatever the
 * wire delivers, no post-processing.
 *
 * Error-shape contract comes from Task 057 (Rate Limits and Abuse Controls):
 *   - 429 rate_limit_exceeded { retry_after_seconds, message }
 *   - 400 query_too_long { max_length, message }
 *   - 400 too_many_sources { max_sources, message }
 * Network failures and unexpected shapes map to "network" / "unknown" so
 * the reducer has one finite set of failure kinds to switch on.
 */

export interface AnswerCitation {
  title: string;
  path: string;
  collection: string;
  book?: string;
  chapter?: number;
  content_kind?: "book" | "short";
  entry_id?: string;
  video_id?: string;
  topic_ids?: string[];
  url?: string;
  timestamp_start?: number;
  timestamp_end?: number;
  // Optional short passage preview (Task 077), verbatim-clipped from the
  // retrieved match text. The UI renders whatever the wire delivers.
  excerpt?: string;
  // Task 190: Corpus Reader anchor `{slug}-{timestamp}` emitted by the RAG, so a
  // source card can deep-link to /corpus/{book}/{chapter}#{reader_anchor}. Absent
  // when not derivable → the reader link is hidden.
  reader_anchor?: string;
}

export interface AnswerResponse {
  answer: string;
  sources: AnswerCitation[];
  route: {
    type: string;
    references: Array<{
      book: string;
      chapter: number;
      verse?: number;
      verseEnd?: number;
    }>;
  };
  cache: {
    hit: boolean;
    key_hash: string;
    layer?: "l1_exact" | "qdrant_exact" | "qdrant_semantic";
    lookup_ms?: number;
    semantic_candidate?: boolean;
    semantic_score_bucket?: string;
    semantic_judge?: "approved" | "rejected" | "timeout" | "error" | "not_run";
  };
  model: {
    name: string;
    provider: string;
  };
  usage?: { input_tokens?: number; output_tokens?: number };
  /**
   * Task 170 (condense-then-answer): present ONLY when the server rewrote an
   * elliptical follow-up into a standalone question. The UI shows a subtle
   * "interpreté tu pregunta como…" affordance. Absent on the normal single-turn
   * path, so existing rendering is unaffected.
   */
  rewrite?: {
    applied: boolean;
    standalone_query: string;
  };
  /**
   * Task 251 Layer 2: the proxy's X-Request-ID for this answer, read from the
   * response header. Reader feedback is attached to it; absent when the server
   * did not send one (direct-RAG dev), and then no feedback controls render.
   */
  request_id?: string;
}

/** The answer's id from the X-Request-ID response header, when well-formed.
 *  Never throws: feedback is optional and must not be able to break answering. */
function readRequestId(response: Response): string | undefined {
  try {
    const value = response.headers?.get("X-Request-ID");
    return typeof value === "string" && /^[0-9a-f-]{36}$/i.test(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** A prior visible turn sent (rewrite-only) to resolve a follow-up (Task 170). */
export interface RewriteContextTurn {
  role: "user" | "assistant";
  text: string;
}

export type AnswerErrorKind =
  | "rate_limit"
  | "query_too_long"
  | "too_many_sources"
  | "network"
  // The answer provider (or the RAG behind it) is down/over-budget right now:
  // pre-stream 402/5xx, or a terminal mid-stream provider failure. Distinct from
  // "network" (the browser's own fetch failed) and "unknown" (a shape we can't
  // classify) so the reader gets a warm "temporarily unavailable, try again" copy.
  | "service_unavailable"
  | "unknown";

export interface AnswerError {
  /** Reject provisional text when final validation or provenance fails. */
  discardPartial?: boolean;
  kind: AnswerErrorKind;
  // Server-supplied retry hint for rate_limit; absent otherwise.
  retryAfterSeconds?: number;
  // Server-supplied limit hint for query_too_long / too_many_sources.
  limit?: number;
}

export type AnswerResult =
  | { ok: true; data: AnswerResponse }
  | { ok: false; error: AnswerError };

export interface PostAnswerOptions {
  language?: string;
  signal?: AbortSignal;
  /** Task 170: prior-turn context for the rewrite pre-step (rewrite-only). */
  context?: RewriteContextTurn[];
  /** Task 170: per-request rewrite override (false = "use my exact words"). */
  rewrite?: boolean;
  /** Task 196: this question came from a shared /chat?q= link, as_is or edited. */
  shareOrigin?: "as_is" | "edited";
}

interface ServerErrorBody {
  error?: unknown;
  message?: unknown;
  retry_after_seconds?: unknown;
  max_length?: unknown;
  max_sources?: unknown;
}

const SERVER_ERROR_KINDS: Record<string, AnswerErrorKind> = {
  rate_limit_exceeded: "rate_limit",
  query_too_long: "query_too_long",
  too_many_sources: "too_many_sources",
};

function parseInteger(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.floor(value);
  }
  return undefined;
}

function classifyServerError(status: number, body: ServerErrorBody): AnswerError {
  const errorString = typeof body.error === "string" ? body.error : "";
  const mapped = SERVER_ERROR_KINDS[errorString];
  if (mapped === "rate_limit") {
    const retry = parseInteger(body.retry_after_seconds);
    if (retry !== undefined) {
      return { kind: "rate_limit", retryAfterSeconds: retry };
    }
    return { kind: "rate_limit" };
  }
  if (mapped === "query_too_long") {
    const max = parseInteger(body.max_length);
    if (max !== undefined) {
      return { kind: "query_too_long", limit: max };
    }
    return { kind: "query_too_long" };
  }
  if (mapped === "too_many_sources") {
    const max = parseInteger(body.max_sources);
    if (max !== undefined) {
      return { kind: "too_many_sources", limit: max };
    }
    return { kind: "too_many_sources" };
  }
  if (status === 429) {
    const retry = parseInteger(body.retry_after_seconds);
    if (retry !== undefined) {
      return { kind: "rate_limit", retryAfterSeconds: retry };
    }
    return { kind: "rate_limit" };
  }
  // Payment-required (provider wallet drained) or any 5xx → the service is down
  // right now, not the user's request. Warm, retryable copy instead of "unknown".
  if (status === 402 || status >= 500) {
    return { kind: "service_unavailable" };
  }
  return { kind: "unknown" };
}

/**
 * Map a terminal mid-stream `answer.error` event (shape `{ error, kind, ... }`
 * emitted by rag/retrieve.ts) to an AnswerError. The RAG's provider-error kinds
 * (auth / timeout / content_policy / invalid_response / network / unknown) all
 * mean "the answer service failed to produce a response" from the reader's POV,
 * so they collapse to one warm, retryable `service_unavailable`. rate_limit keeps
 * its own countdown copy.
 */
function classifyStreamError(data: unknown): AnswerError {
  const obj = asObject(data);
  const kind = obj && typeof obj.kind === "string" ? obj.kind : "";
  if (kind === "rate_limit") {
    const retry = parseInteger(obj?.retry_after_seconds);
    return retry !== undefined
      ? { kind: "rate_limit", retryAfterSeconds: retry }
      : { kind: "rate_limit" };
  }
  return { kind: "service_unavailable", discardPartial: kind === "invalid_response" || kind === "corpus_changed" };
}

function isAnswerResponse(value: unknown): value is AnswerResponse {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.answer !== "string") return false;
  if (!Array.isArray(candidate.sources)) return false;
  if (typeof candidate.route !== "object" || candidate.route === null) return false;
  if (typeof candidate.cache !== "object" || candidate.cache === null) return false;
  if (typeof candidate.model !== "object" || candidate.model === null) return false;
  return true;
}

/**
 * Fetch /answer. Returns a discriminated union so the caller (composer
 * submit handler) can switch on the result without try/catch ceremony.
 *
 * NOTE on Effect discipline: this function is a plain async function. It
 * does NOT touch React state. The submit handler in App.tsx awaits this
 * and dispatches a reducer action with the result. There is no Effect
 * watching a "pending request" flag.
 */
export async function postAnswer(
  endpoint: string,
  query: string,
  options: PostAnswerOptions = {},
): Promise<AnswerResult> {
  if (typeof endpoint !== "string" || endpoint.length === 0) {
    return { ok: false, error: { kind: "unknown" } };
  }

  const body: Record<string, unknown> = { query };
  if (typeof options.language === "string") {
    body.language = options.language;
  }
  if (Array.isArray(options.context) && options.context.length > 0) {
    body.context = { turns: options.context };
  }
  if (typeof options.rewrite === "boolean") {
    body.rewrite = options.rewrite;
  }
  if (options.shareOrigin) body.share_origin = options.shareOrigin;

  let response: Response;
  try {
    const fetchInit: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };
    if (options.signal !== undefined) {
      fetchInit.signal = options.signal;
    }
    response = await fetch(endpoint, fetchInit);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") {
      // Aborts are not surfaced as errors; the caller decides what to do
      // with the discarded request (typically: nothing, the user moved on).
      return { ok: false, error: { kind: "network" } };
    }
    return { ok: false, error: { kind: "network" } };
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return { ok: false, error: { kind: response.ok ? "unknown" : "network" } };
  }

  if (!response.ok) {
    const body = (parsed ?? {}) as ServerErrorBody;
    return { ok: false, error: classifyServerError(response.status, body) };
  }

  if (!isAnswerResponse(parsed)) {
    return { ok: false, error: { kind: "unknown" } };
  }

  const requestId = readRequestId(response);
  return { ok: true, data: requestId === undefined ? parsed : { ...parsed, request_id: requestId } };
}

// --- Streaming client (Task 137) -----------------------------------------

/**
 * Callbacks the streaming client invokes as SSE events arrive. The submit
 * handler in App.tsx wires these to reducer dispatches. The client never
 * touches React state itself (same Effect-discipline contract as postAnswer).
 */
export interface AnswerStreamHandlers {
  /** A content token/string delta from `answer.delta`. */
  onDelta: (text: string) => void;
  /**
   * The terminal `answer.final`. `response.answer` is the full text assembled
   * from the deltas (the wire's final event carries metadata only — sources,
   * model, route, cache — never the answer text again).
   */
  onFinal: (response: AnswerResponse) => void;
  /**
   * A terminal failure: a pre-stream HTTP error (mapped like postAnswer), a
   * mid-stream `answer.error`, a network drop, or an incomplete stream. NOT
   * called on a clean caller-initiated abort (the caller moved on).
   */
  onError: (error: AnswerError) => void;
  /**
   * A non-text `answer.status`: the fast answer was refused and a slower,
   * careful second pass has started (`stage: "recheck"`). Optional; older
   * servers never send it.
   */
  onStatus?: (status: { stage: "recheck"; reason: string }) => void;
}

export interface PostAnswerStreamOptions {
  language?: string;
  signal?: AbortSignal;
  /** Task 170: prior-turn context for the rewrite pre-step (rewrite-only). */
  context?: RewriteContextTurn[];
  /** Task 170: per-request rewrite override (false = "use my exact words"). */
  rewrite?: boolean;
  /** Task 196: this question came from a shared /chat?q= link, as_is or edited. */
  shareOrigin?: "as_is" | "edited";
}

function asObject(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Assemble an AnswerResponse from the terminal `answer.final` metadata plus the
 * text accumulated from deltas. Tolerant of missing nested fields so a thin
 * final event still yields a renderable response.
 */
function buildResponseFromFinal(finalData: unknown, assembledAnswer: string): AnswerResponse {
  const obj = asObject(finalData) ?? {};
  const route = asObject(obj.route);
  const cache = asObject(obj.cache);
  const model = asObject(obj.model);
  return {
    answer: assembledAnswer,
    sources: Array.isArray(obj.sources) ? (obj.sources as AnswerCitation[]) : [],
    route: {
      type: route && typeof route.type === "string" ? route.type : "",
      references: route && Array.isArray(route.references) ? (route.references as AnswerResponse["route"]["references"]) : [],
    },
    cache: {
      hit: cache ? cache.hit === true : false,
      key_hash: cache && typeof cache.key_hash === "string" ? cache.key_hash : "",
      ...(cache && (cache.layer === "l1_exact" || cache.layer === "qdrant_exact" || cache.layer === "qdrant_semantic")
        ? { layer: cache.layer }
        : {}),
      ...(cache && typeof cache.lookup_ms === "number" ? { lookup_ms: cache.lookup_ms } : {}),
      ...(cache && typeof cache.semantic_candidate === "boolean"
        ? { semantic_candidate: cache.semantic_candidate }
        : {}),
      ...(cache && typeof cache.semantic_score_bucket === "string"
        ? { semantic_score_bucket: cache.semantic_score_bucket }
        : {}),
      ...(cache &&
      (cache.semantic_judge === "approved" || cache.semantic_judge === "rejected" ||
        cache.semantic_judge === "timeout" || cache.semantic_judge === "error" ||
        cache.semantic_judge === "not_run")
        ? { semantic_judge: cache.semantic_judge }
        : {}),
    },
    model: {
      name: model && typeof model.name === "string" ? model.name : "",
      provider: model && typeof model.provider === "string" ? model.provider : "",
    },
    ...(() => {
      const usage = asObject(obj.usage);
      if (!usage) return {};
      const input = typeof usage.input_tokens === "number" ? usage.input_tokens : undefined;
      const output = typeof usage.output_tokens === "number" ? usage.output_tokens : undefined;
      return input === undefined && output === undefined
        ? {}
        : { usage: { ...(input === undefined ? {} : { input_tokens: input }), ...(output === undefined ? {} : { output_tokens: output }) } };
    })(),
    // Task 170: surface the rewrite block when the server applied one.
    ...(() => {
      const rw = asObject(obj.rewrite);
      if (rw && rw.applied === true && typeof rw.standalone_query === "string") {
        return { rewrite: { applied: true, standalone_query: rw.standalone_query } };
      }
      return {};
    })(),
  };
}

/** Parse one SSE frame (event:/data: lines) into {event, data}. */
function parseSseFrame(frame: string): { event: string; data: unknown } | null {
  let event: string | null = null;
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
  }
  if (event === null || dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) };
  } catch {
    return { event, data: null };
  }
}

/**
 * Consume the proxy/RAG `POST /answer/stream` SSE endpoint, invoking handlers
 * as events arrive. Mirrors postAnswer's error taxonomy for the pre-stream
 * (non-2xx JSON) path. Returns when the stream terminates (final/error/abort).
 *
 * Effect discipline: a plain async function invoked from the submit handler.
 * It accumulates the answer text internally so onFinal can deliver a complete
 * AnswerResponse; the caller still receives every delta for live rendering.
 */
export async function postAnswerStream(
  endpoint: string,
  query: string,
  handlers: AnswerStreamHandlers,
  options: PostAnswerStreamOptions = {},
): Promise<void> {
  if (typeof endpoint !== "string" || endpoint.length === 0) {
    handlers.onError({ kind: "unknown" });
    return;
  }

  const body: Record<string, unknown> = { query };
  if (typeof options.language === "string") body.language = options.language;
  if (Array.isArray(options.context) && options.context.length > 0) body.context = { turns: options.context };
  if (typeof options.rewrite === "boolean") body.rewrite = options.rewrite;
  if (options.shareOrigin) body.share_origin = options.shareOrigin;

  let response: Response;
  try {
    const init: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
    };
    if (options.signal !== undefined) init.signal = options.signal;
    response = await fetch(endpoint, init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") return; // caller moved on
    handlers.onError({ kind: "network" });
    return;
  }

  // Pre-stream failure: the proxy/RAG returns JSON (not SSE) for bad payload /
  // rate limit / 5xx. Map exactly like postAnswer.
  if (!response.ok) {
    let parsed: unknown = {};
    try {
      parsed = await response.json();
    } catch {
      /* empty body */
    }
    handlers.onError(classifyServerError(response.status, (parsed ?? {}) as ServerErrorBody));
    return;
  }
  if (response.body === null) {
    handlers.onError({ kind: "unknown" });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const requestId = readRequestId(response);
  let buffer = "";
  let assembled = "";
  let terminal: "final" | "error" | null = null;

  const handleFrame = (frame: string): void => {
    const parsed = parseSseFrame(frame);
    if (parsed === null) return;
    if (parsed.event === "answer.delta") {
      const obj = asObject(parsed.data);
      const text = obj && typeof obj.text === "string" ? obj.text : "";
      if (text.length > 0) {
        assembled += text;
        handlers.onDelta(text);
      }
    } else if (parsed.event === "answer.final") {
      terminal = "final";
      const settled = buildResponseFromFinal(parsed.data, assembled);
      handlers.onFinal(requestId === undefined ? settled : { ...settled, request_id: requestId });
    } else if (parsed.event === "answer.error") {
      terminal = "error";
      handlers.onError(classifyStreamError(parsed.data));
    } else if (parsed.event === "answer.status") {
      const obj = asObject(parsed.data);
      if (obj && obj.stage === "recheck") {
        handlers.onStatus?.({ stage: "recheck", reason: typeof obj.reason === "string" ? obj.reason : "refusal" });
      }
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        handleFrame(frame);
        if (terminal !== null) break;
      }
      if (terminal !== null) break;
    }
  } catch (cause) {
    if (options.signal?.aborted === true) return; // caller moved on
    if (cause instanceof DOMException && cause.name === "AbortError") return;
    if (terminal === null) handlers.onError({ kind: "network" });
    return;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* already released */
    }
  }

  // Stream ended cleanly but produced no terminal event (truncated upstream).
  if (terminal === null && options.signal?.aborted !== true) {
    handlers.onError({ kind: "unknown" });
  }
}
